// RECRUITER CALL — pre-call quote {canCall, priceCredits}.
// path: GET /v3/recruiter-calls/quote?candidateUserId=&jobSlug=
// ***************************************************************************
import { useSwrApi } from '@hooks/useSwrApi/useSwrApi';
import type { ApiSchema } from '@type/api/apiUtilityFn';

interface UseSwrRecruiterCallQuoteProps {
  candidateUserId?: number | null;
  jobSlug?: string | null;
  shouldFetch?: boolean;
}

/**
 * @description SWR: fetch RecruiterCall quote for the button visibility +
 * price tooltip. Backend runs the same entitlement check that gates the
 * actual outgoing webhook, so `canCall === false` reliably means the call
 * would be rejected downstream too.
 */
export const useSwrRecruiterCallQuote = (props: UseSwrRecruiterCallQuoteProps) => {
  const { candidateUserId, jobSlug, shouldFetch = true } = props;

  const query = new URLSearchParams();
  if (candidateUserId) query.set('candidateUserId', String(candidateUserId));
  if (jobSlug) query.set('jobSlug', jobSlug);

  const {
    data, error, isLoading, isValidating, mutate,
  } = useSwrApi<ApiSchema<'RecruiterCallQuote'>>({
    url: `/v3/recruiter-calls/quote?${query.toString()}`,
    shouldFetch: shouldFetch && Boolean(candidateUserId),
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // 1 minute — canCall / price rarely flips
    },
  });

  return {
    data,
    error,
    mutate,
    isLoading,
    isValidating,
  };
};
